import React, { useEffect, useMemo, useState } from "react";

import { faPlus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { EmployeeWithoutGoalsModel } from "@performyard/models";
import { ButtonStandard, Pagination } from "@performyard/py-components";

import DownloadButton from "../../../../../components.smart/DownloadButton/DownloadButton";
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import EmployeesWithoutGoalsTable from "./components/EmployeesWithoutGoalsTable";

import { router } from "../../../../../app/router";

import { useRbacPrimaryRolePermissions } from "../../../../../api/hooks";
import { useCompanySettings } from "../../../../../api/hooks/defs/company";
import {
	useInternalApiSearchService,
	useInternalApiSearchServicePrefetch,
} from "../../../../../api/hooks/defs/search-service";
import { useFeatureFlagQuery } from "../../../../../hooks/useFeatureFlag";

import { getInteralApiSearchServiceCsv } from "../../../../../api/services/defs/internal-api/search-service";

import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import { GOALS_SIDE_NAVIGATION_LINKS } from "../../constants";

const EMPLOYEES_WITHOUT_GOALS_TABLE_LIMIT = 25;

const EmployeesWithoutGoals = ({ navItems }: { navItems: Array<SideNavigationLink> }) => {
	const { data: companySettings, isLoading: isCompanySettingsLoading } = useCompanySettings();
	const { data: permissions } = useRbacPrimaryRolePermissions("goal");
	const [downloadCsvJobID, setDownloadCsvJobID] = useState<string | null>(null);
	const groupsFeatureEnabled = useFeatureFlagQuery("ff-is-groups-enabled", false);
	const { value: groupsEnabled } =
		companySettings?.functions?.groups?.state === "enabled" ? groupsFeatureEnabled : { value: false };

	const canUserCreateGoals = permissions?.includes("goals:createGoal");

	const { pageNumber, setPageNumber, sort, setSort, searchValue, debouncedSearchValue, setDebouncedSearch } =
		useInfoPageTemplateQueryState({
			defaultSortName: "name",
			defaultSortDirection: "asc",
			defaultPageNumber: 1,
		});

	const goalWizardBaseUrl = "/goal/wizard/";

	const queryArgs = useMemo(
		() => ({
			service: "employee" as const,
			filters: {
				name_re: debouncedSearchValue,
				without_goals: true,
			},
			sort: {
				name: sort.name,
				direction: sort.direction,
			},
			skip: (pageNumber - 1) * EMPLOYEES_WITHOUT_GOALS_TABLE_LIMIT,
			limit: EMPLOYEES_WITHOUT_GOALS_TABLE_LIMIT,
		}),
		[debouncedSearchValue, pageNumber, sort]
	);

	const { data, isLoading, refetch } = useInternalApiSearchService(queryArgs);

	useEffect(() => {
		// fetches any new data whenever the page is loaded
		refetch();
	}, []);

	// prefetch 2 pages ahead or before the current page if appropriate
	useInternalApiSearchServicePrefetch(queryArgs, 2);

	const parsedEmployeesResults = useMemo(() => {
		return (data?.results || []).map((employee) => {
			return EmployeeWithoutGoalsModel.parse(employee);
		});
	}, [data]);

	const mainContent = (
		<div>
			<EmployeesWithoutGoalsTable
				employees={parsedEmployeesResults}
				isFetchingEmployees={isCompanySettingsLoading || isLoading}
				handleColumnHeaderClick={(sortValue) => {
					setPageNumber(1);
					setSort(sortValue);
				}}
				activeSort={sort.name}
				activeSortDirection={sort.direction}
				groupsEnabled={groupsEnabled}
			/>
			{!isLoading && data?.total && (
				<div
					data-testid="paginationContainer"
					className="mx-auto mt-5 flex w-full max-w-[400px] justify-center"
				>
					<Pagination
						currentPage={pageNumber}
						pageCount={Math.ceil(
							(data?.total || EMPLOYEES_WITHOUT_GOALS_TABLE_LIMIT) / EMPLOYEES_WITHOUT_GOALS_TABLE_LIMIT
						)}
						onPageChange={(newPage: number) => setPageNumber(newPage)}
					/>
				</div>
			)}
		</div>
	);

	// page template elements:
	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={setDebouncedSearch}
			searchPlaceholder="Search Employees..."
		/>
	);

	const handleDownloadCsv = async () => {
		const response = await getInteralApiSearchServiceCsv({
			service: "employee" as const,
			filters: {
				name_re: debouncedSearchValue,
				without_goals: true,
			},
			sort: {
				name: sort.name,
				direction: sort.direction,
			},
			columns: ["name", ...(groupsEnabled ? ["group"] : []), "manager.name"],
			fileName: `Employees Without Goals${debouncedSearchValue ? ` - Search=${debouncedSearchValue}` : ""}`,
		});

		setDownloadCsvJobID(response.jobID);
	};

	const handleClearCsvDownload = () => {
		setDownloadCsvJobID(null);
	};

	const actionButtons = [
		<DownloadButton
			key="download-csv"
			autoDownload={true}
			jobID={downloadCsvJobID}
			isHollow={true}
			data-testid="downloadCSVButton"
			onClick={handleDownloadCsv}
			onDownload={handleClearCsvDownload}
		>
			Download CSV
		</DownloadButton>,
		canUserCreateGoals && (
			<ButtonStandard
				key="create-goal"
				data-testid="create-goal-button"
				onClick={() => {
					router.navigate(`${goalWizardBaseUrl}`);
				}}
				color="green"
				iconLeft={faPlus}
			>
				Create Goal
			</ButtonStandard>
		),
	];

	return (
		<InfoPageTemplate
			mainContent={mainContent}
			pageTitle="Company Goals"
			navigationLinks={navItems}
			actionButtons={actionButtons}
			toolbarActionRow={toolbarActionRow}
			activeNavigationLinkKey={GOALS_SIDE_NAVIGATION_LINKS[3].key}
		/>
	);
};

export default EmployeesWithoutGoals;
